import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Caption1, MessageBar, MessageBarBody, Spinner, tokens } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import type { RoleDetailDto, RoleGroupKind, RoleSummaryDto } from '../api/types';

const COLOR = {
  center: '#5B5FC7',
  role: '#5B5FC7',
  user: '#D85A30',
  groupPrincipal: '#8764B8',
  Access: '#BA7517',
  Intune: '#1D9E75',
  Group: '#378ADD',
  AppRole: '#C239B3',
} as const;

const initials = (s: string) =>
  (s || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

interface GNode {
  id: string;
  label: string;
  glyph: string;
  color: string;
  cls: string;
  x: number;
  y: number;
  onClick?: () => void;
  onDbl?: () => void;
}
type Edge = { x1: number; y1: number; x2: number; y2: number };

export function IamGraphTab({ onOpen }: { onOpen: (id: string) => void }) {
  const api = useApi();
  const [roles, setRoles] = useState<RoleSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [detail, setDetail] = useState<RoleDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const canvasRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(900);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .getRoleOverview()
      .then((o) => setRoles(o.items))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [api]);

  const focusRole = useCallback(
    async (id: string) => {
      setFocus(id);
      setExpanded(new Set());
      setDetail(null);
      setDetailLoading(true);
      try {
        setDetail(await api.getRole(id));
      } catch {
        /* ignore */
      } finally {
        setDetailLoading(false);
      }
    },
    [api],
  );

  function back() {
    setFocus(null);
    setDetail(null);
    setExpanded(new Set());
  }

  function toggleHub(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const H = 700;
  const focusedRole = focus ? roles.find((r) => r.id === focus) : null;

  const { nodes, edges } = useMemo(() => {
    const nodes: GNode[] = [];
    const edges: Edge[] = [];
    const W = width;
    const cx = W / 2;
    const cy = H / 2;
    const R = Math.min(cx, cy);

    if (focus) {
      // ---- Fokus-Modus: Rolle im Zentrum, typisierte Hubs radial ----
      nodes.push({
        id: 'role',
        label: focusedRole?.name ?? detail?.name ?? 'Rolle',
        glyph: initials(focusedRole?.name ?? detail?.name ?? '?'),
        color: COLOR.role,
        cls: 'role',
        x: cx,
        y: cy,
        onDbl: () => onOpen(focus),
      });

      if (detail) {
        interface Leaf { id: string; label: string; color: string; }
        interface Hub { key: string; label: string; color: string; leaves: Leaf[]; }
        const users = detail.assignments.filter((a) => a.principalType === 'User');
        const grp = detail.assignments.filter((a) => a.principalType === 'Group');
        const byKind = (k: RoleGroupKind) => detail.groups.filter((g) => g.kind === k);
        const hubs: Hub[] = [];
        if (users.length) hubs.push({ key: 'User', label: 'User', color: COLOR.user, leaves: users.map((a) => ({ id: `a:${a.id}`, label: a.principalDisplay, color: COLOR.user })) });
        if (grp.length) hubs.push({ key: 'Group', label: 'Gruppen', color: COLOR.groupPrincipal, leaves: grp.map((a) => ({ id: `a:${a.id}`, label: a.principalDisplay, color: COLOR.groupPrincipal })) });
        const acc = byKind('Access');
        if (acc.length) hubs.push({ key: 'Access', label: 'App-Zugriff', color: COLOR.Access, leaves: acc.map((g) => ({ id: `g:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Access })) });
        const intu = byKind('Intune');
        if (intu.length) hubs.push({ key: 'Intune', label: 'Intune', color: COLOR.Intune, leaves: intu.map((g) => ({ id: `g:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Intune })) });
        const dir = byKind('Group');
        if (dir.length) hubs.push({ key: 'Direct', label: 'Direkte Gruppe', color: COLOR.Group, leaves: dir.map((g) => ({ id: `g:${g.id}`, label: g.appLabel ?? g.groupName, color: COLOR.Group })) });
        if (detail.appRoles.length) hubs.push({ key: 'AppRole', label: 'App-Rolle', color: COLOR.AppRole, leaves: detail.appRoles.map((ar) => ({ id: `ar:${ar.id}`, label: ar.appRoleName ? `${ar.appName} · ${ar.appRoleName}` : ar.appName, color: COLOR.AppRole })) });

        const M = Math.max(1, hubs.length);
        const R1 = R * 0.4;
        const R2 = R * 0.74;
        hubs.forEach((hub, i) => {
          const ang = ((-90 + (i * 360) / M) * Math.PI) / 180;
          const hx = cx + R1 * Math.cos(ang);
          const hy = cy + R1 * Math.sin(ang);
          const open = expanded.has(hub.key);
          nodes.push({
            id: `hub:${hub.key}`,
            label: hub.label,
            glyph: String(hub.leaves.length),
            color: hub.color,
            cls: `hub${open ? ' open' : ''}`,
            x: hx,
            y: hy,
            onClick: () => toggleHub(hub.key),
          });
          edges.push({ x1: cx, y1: cy, x2: hx, y2: hy });

          if (open) {
            const items = hub.leaves.slice(0, 24);
            const count = items.length;
            const sector = (2 * Math.PI) / M;
            const arc = Math.min(sector * 0.82, (Math.min(150, Math.max(24, count * 11)) * Math.PI) / 180);
            const a0 = ang - arc / 2;
            items.forEach((lf, j) => {
              const a = count > 1 ? a0 + j * (arc / (count - 1)) : ang;
              const lx = cx + R2 * Math.cos(a);
              const ly = cy + R2 * Math.sin(a);
              nodes.push({ id: lf.id, label: lf.label, glyph: initials(lf.label), color: lf.color, cls: 'leaf', x: lx, y: ly });
              edges.push({ x1: hx, y1: hy, x2: lx, y2: ly });
            });
          }
        });
      }
      return { nodes, edges };
    }

    // ---- Übersicht: „Rollen"-Hub + Rollen-Ring ----
    nodes.push({ id: 'center', label: 'Rollen', glyph: String(roles.length), color: COLOR.center, cls: 'role', x: cx, y: cy });
    const N = Math.max(1, roles.length);
    const R1 = R * 0.62;
    roles.forEach((r, i) => {
      const ang = ((-90 + (i * 360) / N) * Math.PI) / 180;
      const rx = cx + R1 * Math.cos(ang);
      const ry = cy + R1 * Math.sin(ang);
      nodes.push({
        id: `role:${r.id}`,
        label: r.name,
        glyph: initials(r.name),
        color: COLOR.role,
        cls: 'resource',
        x: rx,
        y: ry,
        onClick: () => void focusRole(r.id),
        onDbl: () => onOpen(r.id),
      });
      edges.push({ x1: cx, y1: cy, x2: rx, y2: ry });
    });
    return { nodes, edges };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, focus, detail, expanded, width, focusedRole]);

  if (loading) return <Spinner label="Lade Rollen-Graph…" />;
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, minHeight: 32 }}>
        {focus ? (
          <Button size="small" appearance="subtle" icon={<ArrowLeftRegular />} onClick={back}>
            Zurück zur Übersicht
          </Button>
        ) : null}
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {focus
            ? 'Klick auf einen Hub klappt seine Einträge auf/zu, Doppelklick auf die Rolle öffnet den Editor.'
            : 'Klick auf eine Rolle rückt sie in den Fokus, Doppelklick öffnet sie im Editor.'}
        </Caption1>
        {detailLoading ? <Spinner size="tiny" /> : null}
      </div>

      <div className="iam-canvas" ref={canvasRef} style={{ height: H }}>
        <svg className="iam-edges">
          {edges.map((e, i) => (
            <line key={i} className="iam-edge" x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} />
          ))}
        </svg>
        {nodes.map((n) => (
          <div
            key={n.id}
            className={`iam-node ${n.cls} side-${n.cls.includes('role') ? 'center' : n.x < width / 2 - 6 ? 'left' : 'right'}`}
            style={{ left: n.x, top: n.y, cursor: n.onClick || n.onDbl ? 'pointer' : 'default' }}
            title={n.label}
            onClick={n.onClick}
            onDoubleClick={n.onDbl}
          >
            <div className="iam-circ" style={{ color: n.color, border: `2px solid ${n.color}` }}>
              {n.glyph}
            </div>
            <div className="iam-lbl pii">{n.label}</div>
          </div>
        ))}
        {!focus && roles.length === 0 ? (
          <div className="iam-empty-canvas">
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Noch keine Rollen angelegt.</Caption1>
          </div>
        ) : null}
      </div>
    </div>
  );
}
